/**
 * Where the previous application ("prev") lives after the domain swap.
 *
 * After the swap web-next is served from `certhon.app` (staging
 * `qa-dev.certhon.app`) and prev from `prev.certhon.app` (staging
 * `prev-dev.certhon.app`). The deployment does not pass build args to the
 * frontend image, so production relies on the hostname derivation below;
 * `VITE_PREV_URL` is for development.
 *
 * Scanned QR labels (`/qr/location/<uuid>`) are forwarded to prev by nginx
 * on the canonical host, but a device with the offline service worker
 * installed never reaches nginx for a navigation, so the router does the
 * same (`qrLocationPrevUrl`).
 */

/** Hostnames that web-next no longer lives on; the app moves the user. */
export const RETIRED_HOSTNAMES = ['next.certhon.app', 'next-dev.certhon.app'];

/** Canonical origin for a retired hostname. */
export function canonicalOriginFor(hostname: string): string | null {
  switch (hostname.toLowerCase()) {
    case 'next.certhon.app':
      return 'https://certhon.app';
    case 'next-dev.certhon.app':
      return 'https://qa-dev.certhon.app';
    default:
      return null;
  }
}

export function isRetiredHostname(hostname: string): boolean {
  return RETIRED_HOSTNAMES.includes(hostname.toLowerCase());
}

/**
 * The same page on the canonical origin when `location` is on a retired
 * hostname, else `null`.
 */
export function retiredOriginTarget(location: {
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  const origin = canonicalOriginFor(location.hostname);
  if (!origin) {
    return null;
  }
  return `${origin}${location.pathname}${location.search}${location.hash}`;
}

/** Derive the prev origin from the hostname web-next is served from. */
export function derivePrevOrigin(hostname: string): string {
  switch (hostname.toLowerCase()) {
    case 'certhon.app':
    case 'next.certhon.app':
      return 'https://prev.certhon.app';
    case 'qa-dev.certhon.app':
    case 'next-dev.certhon.app':
      return 'https://prev-dev.certhon.app';
    default:
      return 'https://prev.certhon.test';
  }
}

/**
 * Origin of the previous application for the environment this build runs
 * in. `VITE_PREV_URL` (build-time) wins when set.
 */
export function getPrevOrigin(
  hostname: string = window.location.hostname,
  override: string | undefined = import.meta.env.VITE_PREV_URL as
    | string
    | undefined,
): string {
  if (override) {
    return override.replace(/\/+$/, '');
  }
  return derivePrevOrigin(hostname);
}

const QR_LOCATION_PATH = /^\/qr\/location\/[^/]+\/?$/;

/**
 * Location labels encode `https://certhon.app/qr/location/<uuid>` and
 * inventory lives in prev. Returns the same URL on prev for such a path, or
 * `null`. Prev's own `/qr/location` route does the last hop to the location.
 */
export function qrLocationPrevUrl(
  pathname: string,
  search = '',
  prevOrigin: string = getPrevOrigin(),
): string | null {
  if (!QR_LOCATION_PATH.test(pathname)) {
    return null;
  }
  return `${prevOrigin}${pathname}${search}`;
}
